// eslint-disable-next-line
import { types, flow, Instance } from 'mobx-state-tree';
// eslint-disable-next-line
import { AxiosResponse } from 'axios';
import axios from 'server';
// eslint-disable-next-line
import { UserProfileModel, IUserProfileModel, UserOrganization } from './models/user.model';
import { UserProfileInfoModel } from './models/userProfileInfo.model';
import {
  AdminProfileInfoModel,
  ClientActivationModel,
  IAdminProfileInfoModel,
  IAdminPasswordInfoModel,
} from './models/adminprofile.model';
import { saveToken, removeToken } from 'utils';
import {
  EndUserProfileInfoModel,
  IEndUserProfileInfoModel,
  IEndUserPasswordInfoModel,
} from './models/enduserprofile.model';
import { EndUserNotificationModel, IEndUserNotificationModel } from './models/endusernotification.model';
import { removeOrganization, saveOrganization } from 'utils/organizationUtils';
import { removeDomain, saveDomain } from 'utils/domainUtils';
import { PusherInstance } from 'utils/pusherUtills';

const { model, string, maybeNull, boolean } = types;

export const UserStore = model('UserStore', {
  user: maybeNull(UserProfileModel),
  userProfileInfo: maybeNull(UserProfileInfoModel),
  profileInfo: maybeNull(AdminProfileInfoModel),
  endUserProfileInfo: maybeNull(EndUserProfileInfoModel),
  clientAWSRegionInfo: maybeNull(ClientActivationModel),
  endUserNotification: maybeNull(EndUserNotificationModel),
  token: string,
  organizationName: string,
  domain: string,
  newEndUserNotificationUpdate: boolean,
  loginStatusCall: boolean,
})
  .views((self) => ({
    isAccessPermited(componentPermissions: string[]): boolean {
      const regexp = new RegExp(componentPermissions?.sort().toString(), 'i');
      return regexp.test(self.user?.allPermissions.toString() as string);
    },
    get getNewEndUserNotificationUpdate(): boolean {
      return self.newEndUserNotificationUpdate;
    },
  }))
  .volatile(() => ({}))
  .actions((self) => ({
    normalizeStore({ data }: AxiosResponse, type: string) {
      // Handle "User not found" case - this is a successful response, not an error
      if (data?.status === 'User not found') {
        return data;
      }
      if (data?.data?.status === 'Client not found') {
        return data;
      }
      localStorage.setItem('Password', JSON.stringify(data?.data?.passwordSet ?? true));
      localStorage.setItem('Profile', JSON.stringify(data?.data?.user));

      if (data?.data?.user?.allPermissions) {
        data.data.user.allPermissions = data.data.user.allPermissions.sort();
      }
      self.user = data?.data?.user;
      self.token = data?.data?.token;
      self.organizationName = data?.data?.organizationName;
      self.domain = data?.data?.user?.hostName || '';

      type === 'user' && localStorage.setItem('type', type);
      saveToken(self.token);
      saveOrganization(self.organizationName);
      saveDomain(self.domain);
      return self.userProfileInfo;
    },
    normalizeAWSRegionInfo({ data }: AxiosResponse): any {
      self.clientAWSRegionInfo = data;
      return self.clientAWSRegionInfo;
    },
    loadUserProfileFromStorage(profileData: any): IUserProfileModel | null {
      const normalizedUser = {
        id: profileData.id || 0,
        name: profileData.name || '',
        email: profileData.email || '',
        lastName: profileData.lastName || null,
        status: profileData.status || 1,
        permission: profileData.permission || '',
        profileUrl: profileData.profileUrl || null,
        allPermissions: profileData.allPermissions || [],
        promptMessage: profileData.promptMessage || null,
        organizationName: profileData.organizationName || null,
        passwordSet: profileData.passwordSet !== undefined ? profileData.passwordSet : true,
        managed: profileData.managed || [],
        roles: profileData.roles || null,
      };
      self.user = normalizedUser as any;
      return self.user;
    },
    normalizeUserProfile({ data }: AxiosResponse): IUserProfileModel | null {
      const userData = data?.data || data;
      const existingProfile = JSON.parse(localStorage.getItem('Profile') || '{}');
      const permissions =
        userData.allPermissions || existingProfile.allPermissions || (userData.permission ? [userData.permission] : []);
      const normalizedUser = {
        id: userData.id || existingProfile.id || 0,
        name: userData.firstName || userData.name || existingProfile.name || '',
        email: userData.email || existingProfile.email || '',
        lastName: userData.lastName || existingProfile.lastName || null,
        status: userData.status || existingProfile.status || 1,
        permission: userData.permission || existingProfile.permission || '',
        profileUrl: userData.profileUrl || existingProfile.profileUrl || null,
        allPermissions: permissions,
        promptMessage: userData.promptMessage || existingProfile.promptMessage || null,
        organizationName: userData.organizationName || existingProfile.organizationName || null,
        passwordSet:
          userData.passwordSet !== undefined
            ? userData.passwordSet
            : existingProfile.passwordSet !== undefined
            ? existingProfile.passwordSet
            : true,
        managed: userData.managed || existingProfile.managed || [],
        roles: userData.roles || existingProfile.roles || null,
      };
      self.user = normalizedUser as any;
      localStorage.setItem('Profile', JSON.stringify(normalizedUser));
      return self.user;
    },
    normalizeUserProfileInfo({ data }: AxiosResponse): IAdminProfileInfoModel | null {
      const profileData = data?.data || data;

      const normalizedProfile = {
        address: profileData.address || null,
        password: profileData.password || null,
        profileUrl: profileData.profileUrl || undefined,
        confirm_password: profileData.confirm_password || null,
        oldPassword: profileData.oldPassword || null,
        billingContactInformationDepartment: profileData.billingContactInformationDepartment || null,
        billingContactInformationEmail: profileData.billingContactInformationEmail || profileData.email || '',
        billingContactInformationFullName: profileData.billingContactInformationFullName || profileData.fullName || '',
        billingContactInformationPhone: profileData.billingContactInformationPhone || null,
        billingContactInformationTitle: profileData.billingContactInformationTitle || null,
        department: profileData.department || null,
        profileImg: profileData.profileImg || undefined,
        email: profileData.email || '',
        firstName: profileData.firstName || '',
        lastName: profileData.lastName || null,
        lastLogin: profileData.lastLogin || null,
        organizationName: profileData.organizationName || null,
        phone: profileData.phone || null,
        salesTaxGstVatExemptOrganization: profileData.salesTaxGstVatExemptOrganization || 0,
        title: profileData.title || null,
      };

      self.profileInfo = normalizedProfile;
      return self.profileInfo;
    },
    normalizeEndUserProfileInfo({ data }: AxiosResponse): IEndUserProfileInfoModel | null {
      // Get existing profile from localStorage to preserve important fields
      const existingProfile = JSON.parse(localStorage.getItem('Profile') || '{}');

      // Convert arrays to strings for MobX compatibility
      const processedData = {
        ...data,
        groups: Array.isArray(data.groups) ? (data.groups.length > 0 ? data.groups.join(',') : null) : data.groups,
        departments: Array.isArray(data.departments)
          ? data.departments.length > 0
            ? data.departments.join(',')
            : null
          : data.departments,
      };

      // Merge API data with existing profile, preserving important fields
      const enrichedData = {
        ...processedData,
        // Preserve these fields from existing profile if they exist
        id: processedData.id || existingProfile.id,
        allPermissions: processedData.allPermissions || existingProfile.allPermissions || [],
        permission: processedData.permission || existingProfile.permission || '',
        status:
          processedData.status !== undefined
            ? processedData.status
            : existingProfile.status !== undefined
            ? existingProfile.status
            : 1,
        passwordSet:
          processedData.passwordSet !== undefined
            ? processedData.passwordSet
            : existingProfile.passwordSet !== undefined
            ? existingProfile.passwordSet
            : true,
        managed: processedData.managed || existingProfile.managed || [],
        roles: processedData.roles || existingProfile.roles || null,
        name: processedData.name || existingProfile.name || processedData.firstName || '',
      };
      localStorage.setItem('Profile', JSON.stringify(enrichedData));
      self.endUserProfileInfo = enrichedData;
      return self.endUserProfileInfo;
    },
    normalizeEndUserNotificationInfo({ data }: AxiosResponse): IEndUserNotificationModel | null {
      const notificationData = data?.data || data;

      const normalizedNotification = {
        count: Array.isArray(notificationData) ? notificationData.length : notificationData?.count || 0,
      };

      self.endUserNotification = normalizedNotification;
      return self.endUserNotification;
    },
    setNewEndUserNotificationUpdate(setUpdate: boolean) {
      self.newEndUserNotificationUpdate = setUpdate;
    },
    clearStore() {
      PusherInstance.unsubscribe('profile-request');
      PusherInstance.unsubscribe('profile-request-count');
      self.user = null;
      self.token = '';
      self.organizationName = '';
      self.domain = '';
      removeOrganization();
      removeDomain();
      removeToken();

      return self;
    },
  }))
  .actions((self) => ({
    login: flow(function* (data) {
      return yield axios
        .post(`auth/login`, data)
        .then((res) => {
          self.normalizeStore(res, data.type);
          return res;
        })
        .catch((error) => {
          const errorMessage = error?.response?.data?.message || error?.response?.data?.data?.message || 'Login failed';
          throw new Error(errorMessage);
        });
    }),
    register: flow(function* (data) {
      return yield axios
        .post(`auth/register`, data)
        .then((res) => {
          return res;
        })
        .catch((error) => {
          throw new Error(error.response?.data?.message || 'Registration failed');
        });
    }),

    SaveCode: flow(function* () {
      return yield axios
        .post(`auth/2fa/recovery/save`)
        .then((res) => {
          return res;
        })
        .catch((error) => {
          throw new Error(error.response.data.data.message);
        });
    }),
    loginEnable: flow(function* (data) {
      const { fqdn, token } = data;
      return yield axios
        .post('auth/2fa/enable', { fqdn, token })
        .then((res) => {
          return res;
        })
        .catch((error) => {
          throw new Error(error.response.data.data.message);
        });
    }),
    googlelogin: flow(function* () {
      return yield axios
        .get(`auth/social/google`)
        .then((res) => {
          return res.data;
        })
        .catch((error) => {
          throw new Error(error.response.data.data.message);
        });
    }),

    googleAuth: flow(function* (location, userType: string) {
      return yield axios
        .post(`auth/social/google/callback${location}`)
        .then((res) => {
          self.normalizeStore(res, userType);
          return res;
        })
        .catch((error) => {
          const errorMessage =
            error?.response?.data?.data?.message ||
            error?.response?.data?.message ||
            error?.message ||
            'Google authentication failed';
          throw new Error(errorMessage);
        });
    }),

    // googleRegister: flow(function* (location, userType: string) {
    //   return yield axios
    //     .post(`auth/google-registration/callback${location}`)
    //     .then((res) => {
    //       self.normalizeStore(res, userType);
    //       return res;
    //     })
    //     .catch((error) => {
    //       const errorMessage = error?.response?.data?.data?.message ||
    //         error?.response?.data?.message ||
    //         error?.message ||
    //         'Google authentication failed';
    //       throw new Error(errorMessage);
    //     });
    // }),
    googleRegister: flow(function* (location, userType: string) {
      return yield axios
        .post(`auth/google-registration/callback${location}`)
        .then((res) => {
          self.normalizeStore(res, userType);

          // Backend ka actual response return karo
          return res.data;
        })
        .catch((error) => {
          const errorMessage =
            error?.response?.data?.data?.message ||
            error?.response?.data?.message ||
            error?.message ||
            'Google authentication failed';

          throw new Error(errorMessage);
        });
    }),
    logout: flow(function* () {
      const result = yield axios
        .post(`auth/logout`)
        .then(self.clearStore)
        .catch((error) => {
          throw new Error(error);
        });
      return result;
    }),
    deleteAccount: flow(function* () {
      const result = yield axios
        .post(`admin/license-subscription/delete-client-account`, { action: 'delete' })
        .then(function* () {
          yield axios
            .post(`admin/logout`)
            .then(self.clearStore)
            .catch((error) => {
              throw new Error(error);
            });
        })
        .catch((error) => {
          throw new Error(error);
        });
      return result;
    }),

    AuthenticatorVerify: flow(function* (data: { code: string }) {
      try {
        const result = yield axios.post(`auth/2fa/verify`, data).then((res) => {
          return res.data;
        });
        return result;
      } catch (error: any) {
        throw new Error(error.response?.data?.message || error.message || 'Something went wrong');
      }
    }),
    RecoveryCodeVerify: flow(function* (data: { code: string }) {
      try {
        const result = yield axios.post(`auth/2fa/recovery/verify`, data).then((res) => {
          return res.data;
        });
        return result;
      } catch (error: any) {
        throw new Error(error.response?.data?.message || error.message || 'Something went wrong');
      }
    }),
    RecoveryCodeGenerate: flow(function* () {
      try {
        const result = yield axios.post(`auth/2fa/recovery/generate`).then((res) => {
          return res.data;
        });
        return result;
      } catch (error: any) {
        throw new Error(error.response?.data?.message || error.message || 'Something went wrong');
      }
    }),

    getAWSRegion: flow(function* (id: any) {
      const result = yield axios
        .get<any>(`admin/aws-regions/${id}`)
        .then(self.normalizeAWSRegionInfo)
        .catch((error) => {
          throw new Error(error);
        });
      return result;
    }),
    // user/dashboard
    getProfile: flow(function* () {
      const result = yield axios
        .get<IUserProfileModel>(`profile`)
        .then(self.normalizeUserProfile)
        .catch((error) => {
          throw error?.response?.data || error;
        });
      return result;
    }),
    getProfileInfo: flow(function* () {
      const result = yield axios
        .get<IAdminProfileInfoModel>(`profile`)
        .then(self.normalizeUserProfileInfo)
        .catch((error) => {
          throw new Error(error);
        });
      return result;
    }),
    getEndUserProfileInfo: flow(function* () {
      const result = yield axios
        .get<IEndUserProfileInfoModel>(`user/profile`)
        .then(self.normalizeEndUserProfileInfo)
        .catch((error) => {
          throw new Error(error);
        });
      return result;
    }),
    getSystemUserProfileInfo: flow(function* () {
      const result = yield axios
        .get<IEndUserProfileInfoModel>(`user/profile`)
        .then(self.normalizeEndUserProfileInfo)
        .catch((error) => {
          throw new Error(error);
        });
      return result;
    }),
    getEndUserNotification: flow(function* () {
      const result = yield axios
        .get<IEndUserNotificationModel>(`profile-requests/count`)
        .then(self.normalizeEndUserNotificationInfo)
        .catch((error) => {
          throw new Error(error);
        });
      return result;
    }),
    updateDomain: flow(function* (data) {
      const result = yield axios
        .post(`update-domain`, data)
        .then()
        .catch((error) => {
          throw new Error(error.response.data.message);
        });
      return result;
    }),
    updateProfileInfo: flow(function* (data: any) {
      const formData = new FormData();
      Object.keys(data).forEach((key) => {
        formData.append(key, data[key]);
      });
      const result = yield axios
        .post<IAdminProfileInfoModel>(`/profile`, formData)
        .then()
        .catch((error) => {
          throw new Error(error.response.data.data.message);
        });
      return result;
    }),
    updatePasswordInfo: flow(function* (data: any) {
      const formData = new FormData();
      Object.keys(data).forEach((key) => {
        formData.append(key, data[key]);
      });
      // .post<IAdminPasswordInfoModel>(`admin/client-password-update`, formData)
      const result = yield axios
        .post<IAdminPasswordInfoModel>(`auth/password/change`, formData)
        .then()
        .catch((error) => {
          throw new Error(
            error?.response?.data?.data?.message || error?.response?.data?.message || 'Something went wrong'
          );
        });
      return result;
    }),
    updateEndUserPasswordInfo: flow(function* (data: any) {
      const formData = new FormData();
      Object.keys(data).forEach((key) => {
        formData.append(key, data[key]);
      });
      const result = yield axios
        .post<IEndUserPasswordInfoModel>(`auth/password/change`, formData)
        .then()
        .catch((error) => {
          throw new Error(
            error?.response?.data?.data?.message || error?.response?.data?.message || 'Something went wrong'
          );
        });
      return result;
    }),
    updateEndUserProfileInfo: flow(function* (data: any) {
      const formData = new FormData();
      Object.keys(data).forEach((key) => {
        const value = data[key];
        if (value !== undefined && value !== null) {
          if (key === 'profileImg' && value instanceof File) {
            formData.append(key, value);
          } else if (Array.isArray(value)) {
            formData.append(key, JSON.stringify(value));
          } else if (typeof value === 'object' && value !== null) {
            formData.append(key, JSON.stringify(value));
          } else {
            formData.append(key, value);
          }
        }
      });
      try {
        const result = yield axios.post<IEndUserProfileInfoModel>('user/profile-requests', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        return result;
      } catch (error: any) {
        console.error('Error:', error);
        throw new Error(error.response?.data?.message || 'An error occurred');
      }
    }),
  }));

export type IUserStore = Instance<typeof UserStore>;
